/**
 * Budget maths on plain numbers (B-0136): per-row kind (need, want, savings, or debt payment) and per-row period (a monthly or a yearly amount).
 * Pure; synced to the API (/v1/planning/monthly-budget). budgetMath.ts (the page's text-field layer) calls this, so the page and the API cannot disagree.
 *
 *  - A yearly amount counts as amount / 12 a month ("sinking fund": set a little aside each month for an annual bill).
 *  - A debt payment counts in the NEEDS bucket of the 50/30/20 view, because the required minimum is a must-pay cost. Enter only the minimum as debt;
 *    anything extra on top can be a Savings row.
 *  - Existing rows (no period, kinds need/want/savings) give exactly the totals they gave before.
 */
import type { CalcStep } from "@/lib/calc/steps";

export type Kind = "need" | "want" | "savings" | "debt";
export type Period = "month" | "year";
export type Bucket = "need" | "want" | "savings";

const SAVINGS_RE = /saving|invest|retire|emergency|goal|401k|ira\b/i;
const WANT_RE = /subscri|dining|restaurant|entertain|hobb|shopping|travel|vacation|stream|gym|fun\b|gift|coffee|takeout|games?/i;
const DEBT_RE = /credit card|card payment|student loan|car loan|auto loan|loan payment|debt/i;

/** Best guess for rows saved before categories existed, or typed by hand. */
export function inferKind(name: string): Kind {
  if (SAVINGS_RE.test(name)) return "savings";
  if (DEBT_RE.test(name)) return "debt";
  if (WANT_RE.test(name)) return "want";
  return "need";
}

export const bucketOf = (k: Kind): Bucket => (k === "debt" ? "need" : k);
export const monthlyAmount = (amount: number, period: Period = "month"): number => (period === "year" ? amount / 12 : amount);
const r2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

export interface PlanRow { name: string; amount: number; kind?: Kind; period?: Period }
export interface PlanLine { name: string; kind: Kind; bucket: Bucket; period: Period; entered: number; monthly: number }
export interface BudgetPlan {
  income: number; needs: number; wants: number; saved: number;
  /** Of the needs: debt payments. */
  debt: number;
  /** Monthly equivalent of the rows entered per year. */
  yearlySetAside: number;
  /** Everything spent that is not savings. */
  spending: number; leftOver: number;
  lines: PlanLine[];
}

export function planBudget(income: number, rows: PlanRow[]): BudgetPlan {
  const lines: PlanLine[] = rows.map((r) => {
    const kind = r.kind ?? inferKind(r.name), period = r.period ?? "month";
    return { name: r.name, kind, bucket: bucketOf(kind), period, entered: r.amount, monthly: monthlyAmount(r.amount, period) };
  });
  const sum = (f: (l: PlanLine) => boolean) => lines.filter(f).reduce((s, l) => s + l.monthly, 0);
  const needs = sum((l) => l.bucket === "need"), wants = sum((l) => l.bucket === "want"), saved = sum((l) => l.bucket === "savings");
  const spending = needs + wants;
  return {
    income: r2(income), needs: r2(needs), wants: r2(wants), saved: r2(saved), debt: r2(sum((l) => l.kind === "debt")), yearlySetAside: r2(sum((l) => l.period === "year")),
    spending: r2(spending), leftOver: r2(income - spending - saved), lines,
  };
}

/** The working behind the totals. `f` formats money. */
export function budgetSteps(p: BudgetPlan, f: (n: number) => number | string = r2): CalcStep[] {
  const steps: CalcStep[] = [];
  const yearly = p.lines.filter((l) => l.period === "year");
  if (yearly.length) steps.push({ label: "Yearly rows as monthly amounts", formula: "Yearly amount / 12", inputs: Object.fromEntries(yearly.map((l) => [l.name || "Row", f(l.entered)])), result: `${f(p.yearlySetAside)} a month in total` });
  steps.push({ label: "Needs (including debt payments)", formula: "Sum of monthly amounts marked Need or Debt payment", inputs: { needs: f(p.needs - p.debt), "debt payments": f(p.debt) }, result: f(p.needs) });
  steps.push({ label: "Wants", formula: "Sum of monthly amounts marked Want", inputs: {}, result: f(p.wants) });
  steps.push({ label: "Savings", formula: "Sum of monthly amounts marked Savings", inputs: {}, result: f(p.saved) });
  steps.push({ label: "Left over", formula: "Income - needs - wants - savings", inputs: { income: f(p.income), needs: f(p.needs), wants: f(p.wants), savings: f(p.saved) }, result: f(p.leftOver) });
  return steps;
}
